import React, { useState } from 'react';
import { Gift, Smartphone, Music, Palette, Users, Heart, Star, Zap, TrendingUp } from 'lucide-react';

const ForTeensPage = ({ setCurrentPage }) => {
  const [selectedInterest, setSelectedInterest] = useState('all');

  const teenInterests = [
    { id: 'technology', name: 'Teknologi & Gaming', icon: Smartphone, color: 'blue' },
    { id: 'fashion', name: 'Mote & Stil', icon: Star, color: 'pink' },
    { id: 'creative', name: 'Kreativitet & Kunst', icon: Palette, color: 'purple' },
    { id: 'music', name: 'Musikk & Underholdning', icon: Music, color: 'green' },
    { id: 'social', name: 'Sosialt & Venner', icon: Users, color: 'yellow' },
    { id: 'sports', name: 'Sport & Aktivitet', icon: Zap, color: 'orange' }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 via-primary-50 to-accent-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">🧒</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gaver til tenåringer</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Tenåringer er i en spennende fase hvor de utforsker identitet, vennskap og fremtidsdrømmer. 
              Finn gaver som støtter deres selvutvikling og interesser.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-primary-600 text-white rounded-lg hover:from-purple-600 hover:to-primary-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn tenåringsgaver
            </button>
          </div>
        </div>
      </section>

      {/* Understanding Teens */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Forstå tenåringer og deres verden
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                <Heart className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Identitet og uttrykk</h3>
                <p className="text-secondary-600">
                  Tenåringer utforsker hvem de er og hvordan de vil uttrykke seg. 
                  Gaver som støtter deres personlige stil og interesser blir høyt verdsatt.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-lg p-6 border border-primary-100">
                <Users className="w-12 h-12 text-primary-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Sosialt og tilhørighet</h3>
                <p className="text-secondary-600">
                  Venner og sosialt liv er enormt viktig. Gaver som kan deles med venner 
                  eller som hjelper dem å få nye sosiale opplevelser er populære.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-purple-50 rounded-lg p-6 border border-accent-100">
                <TrendingUp className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Fremtidsrettet</h3>
                <p className="text-secondary-600">
                  De tenker på fremtiden og hva de vil bli. Gaver som støtter deres 
                  drømmer og ferdighetsutvikling kan være svært meningsfulle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interest Categories */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Gaver basert på deres interesser
            </h2>

            {/* Interest Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedInterest('all')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedInterest === 'all'
                    ? 'bg-purple-500 text-white border-purple-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-purple-300'
                }`}
              >
                Alle interesser
              </button>
              {teenInterests.map((interest) => (
                <button
                  key={interest.id}
                  onClick={() => setSelectedInterest(interest.id)}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${
                    selectedInterest === interest.id
                      ? `bg-${interest.color}-500 text-white border-${interest.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  <interest.icon className="w-4 h-4" />
                  <span>{interest.name}</span>
                </button>
              ))}
            </div>

            {/* Interest Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {teenInterests.map((interest) => (
                <div
                  key={interest.id}
                  className={`bg-white rounded-lg shadow-sm p-6 border border-secondary-100 hover:shadow-md transition-all ${
                    selectedInterest === 'all' || selectedInterest === interest.id ? 'block' : 'hidden'
                  }`}
                >
                  <interest.icon className={`w-12 h-12 text-${interest.color}-500 mb-4`} />
                  <h3 className="text-lg font-semibold text-secondary-800 mb-3">{interest.name}</h3>
                  
                  {interest.id === 'technology' && (
                    <div className="space-y-2 text-sm text-secondary-600">
                      <p><strong>Gaveideer:</strong></p>
                      <ul className="space-y-1">
                        <li>• Gaming-tilbehør og peripherals</li>
                        <li>• Smartklokker og fitness-trackere</li>
                        <li>• Trådløse ørepropper</li>
                        <li>• Portable ladere og power banks</li>
                        <li>• VR-headset eller gaming-gear</li>
                      </ul>
                    </div>
                  )}

                  {interest.id === 'fashion' && (
                    <div className="space-y-2 text-sm text-secondary-600">
                      <p><strong>Gaveideer:</strong></p>
                      <ul className="space-y-1">
                        <li>• Trendy klær og tilbehør</li>
                        <li>• Sneakers og sko</li>
                        <li>• Smykker og accessories</li>
                        <li>• Skjønhets- og hudpleieprodukter</li>
                        <li>• Mote-abonnement eller styling</li>
                      </ul>
                    </div>
                  )}

                  {interest.id === 'creative' && (
                    <div className="space-y-2 text-sm text-secondary-600">
                      <p><strong>Gaveideer:</strong></p>
                      <ul className="space-y-1">
                        <li>• Digital tegnebrett og stylus</li>
                        <li>• Kunst- og håndverksmaterialer</li>
                        <li>• Kreative kurs og workshops</li>
                        <li>• Kamera og fotoutstyr</li>
                        <li>• Design-software og apper</li>
                      </ul>
                    </div>
                  )}

                  {interest.id === 'music' && (
                    <div className="space-y-2 text-sm text-secondary-600">
                      <p><strong>Gaveideer:</strong></p>
                      <ul className="space-y-1">
                        <li>• Kvalitets hodetelefoner</li>
                        <li>• Musikkinstrumenter</li>
                        <li>• Konsertbilletter</li>
                        <li>• Musikk-produksjon software</li>
                        <li>• Vinyl-plater og spillere</li>
                      </ul>
                    </div>
                  )}

                  {interest.id === 'social' && (
                    <div className="space-y-2 text-sm text-secondary-600">
                      <p><strong>Gaveideer:</strong></p>
                      <ul className="space-y-1">
                        <li>• Opplevelser med venner</li>
                        <li>• Party-spill og aktiviteter</li>
                        <li>• Photobooth-tilbehør</li>
                        <li>• Event-billetter</li>
                        <li>• Sosiale plattform-kreditter</li>
                      </ul>
                    </div>
                  )}

                  {interest.id === 'sports' && (
                    <div className="space-y-2 text-sm text-secondary-600">
                      <p><strong>Gaveideer:</strong></p>
                      <ul className="space-y-1">
                        <li>• Sportsutstyr og -klær</li>
                        <li>• Treningsabonnement</li>
                        <li>• Utendørs aktivitetsutstyr</li>
                        <li>• Sports-relaterte opplevelser</li>
                        <li>• Fitness-apper og -trackere</li>
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Age-Specific Considerations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Alderstilpassede vurderinger
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-lg p-6 border border-primary-100">
                <h3 className="text-xl font-semibold text-secondary-800 mb-4">13-15 år</h3>
                <p className="text-secondary-600 text-sm mb-4">Tidlig tenåringsperiode med fokus på vennskap og utforskning</p>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Enkle teknologi-produkter</li>
                  <li>• Kreative utløp og hobbyer</li>
                  <li>• Sosiale aktiviteter</li>
                  <li>• Grunnleggende personlige produkter</li>
                  <li>• Budsjett: 200-800 kr</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-purple-50 rounded-lg p-6 border border-accent-100">
                <h3 className="text-xl font-semibold text-secondary-800 mb-4">16-17 år</h3>
                <p className="text-secondary-600 text-sm mb-4">Mer selvstendig med større ansvar og fremtidsfokus</p>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Mer avansert teknologi</li>
                  <li>• Førerkort og bil-relatert</li>
                  <li>• Fremtidsplanlegging</li>
                  <li>• Kvalitetsprodukter som varer</li>
                  <li>• Budsjett: 400-1500 kr</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                <h3 className="text-xl font-semibold text-secondary-800 mb-4">18-19 år</h3>
                <p className="text-secondary-600 text-sm mb-4">Overgangen til voksenlivet med økt frihet og ansvar</p>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Voksen-produkter og -tjenester</li>
                  <li>• Utdanning og karrière-støtte</li>
                  <li>• Selvstendighet og hjemmetablering</li>
                  <li>• Investeringer i fremtiden</li>
                  <li>• Budsjett: 500-2000 kr</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Teen Gift Tips */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Tips for suksessrike tenåringsgaver
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                <h3 className="text-lg font-semibold text-secondary-800 mb-4">✅ Gjør dette</h3>
                <ul className="space-y-2 text-secondary-600">
                  <li>• Spør diskret om deres ønsker og interesser</li>
                  <li>• Observer hva vennene deres har og liker</li>
                  <li>• Velg kvalitet fremfor kvantitet</li>
                  <li>• Respekter deres utviklende smak</li>
                  <li>• Gi dem litt selvstendighet i valg (gavekort)</li>
                  <li>• Vær oppmerksom på trender</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                <h3 className="text-lg font-semibold text-secondary-800 mb-4">❌ Unngå dette</h3>
                <ul className="space-y-2 text-secondary-600">
                  <li>• Ikke anta hva de liker basert på stereotyper</li>
                  <li>• Unngå for "barnslige" gaver</li>
                  <li>• Ikke gi klær uten å vite størrelse og stil</li>
                  <li>• Unngå kontroversielle eller sensitive emner</li>
                  <li>• Ikke overse viktigheten av merker de bryr seg om</li>
                  <li>• Unngå for kompliserte eller tidkrevende gaver</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-purple-50 border border-purple-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Star className="w-6 h-6 text-purple-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-purple-800 mb-2">Den perfekte tenåringsgaven</h3>
                  <p className="text-purple-700">
                    Balanserer det de ønsker seg nå med noe som støtter deres vekst og utvikling. 
                    De beste gavene til tenåringer viser at du respekterer hvem de er i ferd med å bli, 
                    samtidig som du støtter deres reise dit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-500 to-primary-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å glede en tenåring?
            </h2>
            <p className="text-xl text-purple-100 mb-8">
              Bruk vår gavefinner for å få anbefalinger som treffer deres unike interesser og personlighet
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-purple-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('recipients')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-purple-600 transition-colors font-semibold"
              >
                Se andre mottakere
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForTeensPage;